/**
 * Navigation Helper Utilities
 * Checks user preferences for opening pages in new windows
 */

/**
 * Get navigation target based on user preferences
 * @param {string} routeType - The type of route (e.g., 'customers', 'quotes')
 * @returns {string} '_blank' if should open in new window, undefined otherwise
 */
export const getNavigationTarget = (routeType) => {
  try {
    const settings = localStorage.getItem('pageSettings');
    if (!settings) return undefined;
    
    const parsed = JSON.parse(settings);
    return parsed[routeType] ? '_blank' : undefined;
  } catch (error) {
    console.error('Error reading page settings:', error);
    return undefined;
  }
};

/**
 * Navigate with user preferences
 * @param {function} navigate - React Router navigate function
 * @param {string} path - The path to navigate to
 * @param {string} routeType - The type of route for preference lookup
 */
export const navigateWithPreference = (navigate, path, routeType) => {
  const target = getNavigationTarget(routeType);
  
  if (target === '_blank') {
    // Open in new window
    window.open(path, '_blank');
  } else {
    // Navigate normally
    navigate(path);
  }
};

/**
 * Get props for a clickable row/card that respects user preferences
 * @param {function} navigate - React Router navigate function  
 * @param {string} path - The path to navigate to
 * @param {string} routeType - The type of route for preference lookup
 * @returns {object} Props to spread on the element
 */
export const getNavigationProps = (navigate, path, routeType) => {
  const target = getNavigationTarget(routeType);
  
  return {
    onClick: (e) => {
      // Check for Ctrl/Cmd+click (user wants new tab)
      if (e.ctrlKey || e.metaKey) {
        window.open(path, '_blank');
        return;
      }
      
      if (target === '_blank') {
        window.open(path, '_blank');
      } else {
        navigate(path);
      }
    },
    style: { cursor: 'pointer' },
    // Add visual indicator if opens in new window
    ...(target === '_blank' ? { 'data-opens-new-window': true } : {})
  };
};
